import { db as firestore } from '../firebase';
import { doc, setDoc, deleteDoc } from 'firebase/firestore';
import dexie from '../offlineDB';

const ENTITY_TO_COLLECTION = {
  tune: 'tunes',
  set: 'sets',
  session: 'sessions',
  preference: 'preferences'
};

const WRITE_TYPES = new Set(['add', 'update', 'delete']);

export function normalizePendingWrite(write) {
  if (!write) return null;

  const rawType = typeof write.type === 'string' ? write.type.toLowerCase() : '';
  const rawOperation = typeof write.operation === 'string' ? write.operation.toLowerCase() : '';

  const operation = WRITE_TYPES.has(rawType)
    ? rawType
    : (WRITE_TYPES.has(rawOperation) ? rawOperation : '');

  const entityCollection = ENTITY_TO_COLLECTION[rawType] || '';
  const collection = write.collection || entityCollection || '';

  const docId = write.docId || (
    // Legacy shape often used `id` as the document id when `docId` was omitted.
    !write.docId && entityCollection ? write.id : ''
  );

  if (!operation || !collection || !docId) return null;

  return {
    queueId: write.id,
    operation,
    collection,
    docId,
    data: write.data || {}
  };
}

export async function flushPendingWrites(user) {
  if (!user?.uid) {
    console.warn('[flushPendingWrites] No valid user — skipping');
    return;
  }

  const writes = await dexie.pendingWrites.toArray();

  for (const write of writes) {
    const normalized = normalizePendingWrite(write);
    if (!normalized) {
      console.warn('⚠️ Skipping malformed pending write:', write);
      await dexie.pendingWrites.delete(write?.id);
      continue;
    }

    const { queueId, operation, collection, docId, data } = normalized;
    const ref = doc(firestore, 'users', user.uid, collection, docId);

    try {
      if (operation === 'add' || operation === 'update') {
        await setDoc(ref, data, { merge: operation === 'update' });

        // Keep local Dexie cache in sync with what was just flushed.
        if (collection === 'tunes') {
          await dexie.tunes.put({ id: docId, ...data });
        } else if (collection === 'sets') {
          await dexie.sets.put({ id: docId, ...data });
        } else if (collection === 'sessions') {
          await dexie.sessions.put({ id: docId, ...data });
        } else if (collection === 'preferences') {
          await dexie.preferences.put({ id: docId, ...data });
        }
      } else if (operation === 'delete') {
        await deleteDoc(ref);

        if (collection === 'tunes') {
          await dexie.tunes.delete(docId);
        } else if (collection === 'sets') {
          await dexie.sets.delete(docId);
        } else if (collection === 'sessions') {
          await dexie.sessions.delete(docId);
        } else if (collection === 'preferences') {
          await dexie.preferences.delete(docId);
        }
      }

      await dexie.pendingWrites.delete(queueId);
      console.log(`✅ Synced ${operation} to ${collection}/${docId}`);
    } catch (err) {
      console.warn('❌ Sync failed for', write, err);
      // retry later
    }
  }
}

export async function addPendingWrite(write) {
  await dexie.pendingWrites.add(write);
}
